import { useNavigate } from 'react-router-dom';
import { Camera, User, Users } from 'lucide-react';
import {
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import { useFamilyScope } from '@/contexts/FamilyScopeContext';

/**
 * The "view as" entries (my account + every family I belong to that I don't
 * own), shared by the scope switcher and the mobile "More" menu so both stay
 * in sync and neither nests one dropdown inside another.
 */
export function ScopeMenuItems({ onPicked }: { onPicked?: () => void }) {
  const { t } = useTranslation('nav');
  const ROLE_LABEL: Record<string, string> = {
    owner: t('scope.role.owner'),
    co_manager: t('scope.role.coManager'),
    contributor: t('scope.role.contributor'),
    scanner: t('scope.role.scanner'),
    viewer: t('scope.role.viewer'),
  };
  const navigate = useNavigate();
  const { scope, setScope, availableFamilies } = useFamilyScope();

  if (availableFamilies.length === 0) return null;

  const active = scope.type === 'family'
    ? availableFamilies.find((f) => f.groupId === scope.groupId)
    : null;

  const pick = (fn: () => void) => {
    fn();
    onPicked?.();
  };

  return (
    <>
      <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {t('scope.viewAs')}
      </DropdownMenuLabel>
      <DropdownMenuItem onClick={() => pick(() => setScope({ type: 'personal' }))} className="text-xs">
        <User className="h-3.5 w-3.5 me-2" />
        {t('scope.myAccount')}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      {availableFamilies.map((f) => (
        <DropdownMenuItem
          key={f.groupId}
          onClick={() =>
            pick(() =>
              setScope({ type: 'family', ownerId: f.ownerId, groupId: f.groupId, groupName: f.groupName }),
            )
          }
          className="text-xs"
        >
          <Users className="h-3.5 w-3.5 me-2" />
          <span className="truncate flex-1">{f.groupName}</span>
          {f.role && (
            <span className="text-[9px] text-muted-foreground/70 ms-2 shrink-0">
              {ROLE_LABEL[f.role] || f.role}
            </span>
          )}
        </DropdownMenuItem>
      ))}
      {active?.role === 'scanner' && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => pick(() => navigate('/scanner'))} className="text-xs">
            <Camera className="h-3.5 w-3.5 me-2" />
            {t('scope.scanForThisFamily')}
          </DropdownMenuItem>
        </>
      )}
    </>
  );
}
